import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  AudioFile as AudioFileIcon,
  PictureAsPdf as PdfIcon,
  TableChart as TableIcon,
  Code as CodeIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Helper to get icon for file type
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();

  switch (ext) {
    case 'pdf':
      return <PdfIcon />;
    case 'doc':
    case 'docx':
    case 'txt':
    case 'md':
      return <DescriptionIcon />;
    case 'xlsx':
    case 'xls':
    case 'csv':
      return <TableIcon />;
    case 'json':
    case 'html':
    case 'xml':
      return <CodeIcon />;
    case 'mp3':
    case 'wav':
    case 'm4a':
    case 'ogg':
    case 'webm':
      return <AudioFileIcon />;
    default:
      return <FileIcon />;
  }
};

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
};

// eslint-disable-next-line max-lines-per-function
const AttachmentsList = ({ noteId, entityId, onDelete }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load attachments
  useEffect(() => {
    const loadAttachments = async () => {
      if (!noteId || !entityId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const result = await api.getNoteAttachments(noteId, entityId);
        setAttachments(result.attachments || []);
      } catch (err) {
        console.error('Failed to load attachments:', err);
        setError('Failed to load attachments');
      } finally {
        setLoading(false);
      }
    };

    loadAttachments();
  }, [noteId, entityId]);

  // Handle delete
  const handleDeleteClick = (attachment) => {
    setAttachmentToDelete(attachment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!attachmentToDelete) return;

    try {
      setDeleting(true);
      await api.deleteNoteAttachment(
        noteId,
        attachmentToDelete.id,
        entityId
      );

      // Remove from list
      setAttachments(attachments.filter(a => a.id !== attachmentToDelete.id));
      toast.success('Attachment deleted');

      // Callback
      if (onDelete) {
        onDelete();
      }

      // Close dialog
      setDeleteDialogOpen(false);
      setAttachmentToDelete(null);
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      toast.error('Failed to delete attachment: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Handle download (if available)
  const handleDownload = (attachment) => {
    if (attachment.downloadUrl) {
      window.open(attachment.downloadUrl, '_blank');
    } else {
      toast.info('Download not available for this attachment');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (attachments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
        No attachments
      </Typography>
    );
  }

  return (
    <>
      <List dense>
        {attachments.map((attachment) => (
          <ListItem
            key={attachment.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              bgcolor: 'background.paper',
            }}
          >
            <ListItemIcon sx={{ color: 'primary.main' }}>
              {getFileIcon(attachment.fileName)}
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ component: 'div' }}
              secondaryTypographyProps={{ component: 'div' }}
              primary={
                <Typography variant="body2" color="text.primary">
                  {attachment.fileName}
                </Typography>
              }
              secondary={
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={formatFileSize(attachment.fileSize)}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  {attachment.uploadedAt && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(attachment.uploadedAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {attachment.downloadUrl && (
                  <Tooltip title="Download">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDownload(attachment)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDeleteClick(attachment)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{attachmentToDelete?.fileName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AttachmentsList;
